import type { AppRouterInstance } from "next/dist/shared/lib/app-router-context.shared-runtime";
import { store } from "@store/index";

import { setCurrent } from "@store/features/account/workplaceSlice";
import { reset as resetSideNavigation } from "@store/features/layout/sideNavigationSlice";
import { close as closeDialog } from "@store/features/layout/dialogSlice";
import { trpc } from "@utils/api";

export { excludeArchivedWorkplaces } from "./excludeArchivedWorkplaces";
export { sortWorkplacesByName } from "./sortWorkplacesByName";

const switchWorkplace = (subdomain: string): boolean => {
  /** get the workplaces from the store */
  const { workplace } = store.getState();

  if (workplace.workplace.data?.subdomain == subdomain) {
    return true;
  }

  /**
   * drop leftover sidebars/dialogs so the next workplace layout does not
   * generate EDIT_FIELD / WORKFLOW_RUN / etc. against the previous workplace
   */
  store.dispatch(resetSideNavigation());
  store.dispatch(closeDialog());

  /**
   * switch to the workplace
   */
  store.dispatch(setCurrent({ subdomain }));

  return true;
};

function getRelativePathFromLocation(
  pathname: string,
  currentSubdomain?: string,
): string {
  if (!currentSubdomain) {
    return "";
  }

  const prefix = `/${currentSubdomain}`;
  if (!pathname.startsWith(prefix)) {
    return "";
  }

  return pathname.slice(prefix.length).replace(/^\//, "");
}

export async function navigateToWorkplace(
  targetSubdomain: string,
  router: AppRouterInstance,
): Promise<void> {
  if (!targetSubdomain) {
    return;
  }

  const currentSubdomain =
    store.getState().workplace.workplace.data?.subdomain ?? undefined;
  const pathname =
    typeof window !== "undefined" ? window.location.pathname : "";
  const search = typeof window !== "undefined" ? window.location.search : "";
  const relativePath = getRelativePathFromLocation(pathname, currentSubdomain);

  switchWorkplace(targetSubdomain);

  try {
    const result = await trpc.workplaces.resolveSwitchPath.query({
      targetSubdomain,
      sourceSubdomain: currentSubdomain,
      relativePath,
      search: search || undefined,
    });
    router.push(result.path);
  } catch {
    router.push(`/${targetSubdomain}/threads`);
  }
}

export default switchWorkplace;
